import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription, catchError, forkJoin, of } from 'rxjs';
import { SeasonManagerService } from 'src/app/services/season-manager.service';
import { SeasonSharedDataService } from 'src/app/services/season-shared-data.service';

/** One field in the order. Only what this screen shows and returns. */
export interface OrderableField {
  readonly id: number;
  readonly fieldName: string;
  readonly farmName: string;
  /**
   * Hectares, from the FIELD and not from any one crop definition on it.
   *
   * Type and crop used to sit here and were removed on purpose. Both belong to a crop definition,
   * not to a field: the season data table edits them per definition, and a field carrying three
   * cultivars has three of each. This list has one row per field, so there was no honest value to
   * print and it showed whichever definition happened to come back first. fieldSize is a property
   * of the field itself, so every definition on it reports the same number.
   *
   * null when the field has no size recorded, which is not the same as nought hectares.
   */
  readonly size: number | null;
}

type SortColumn = '' | 'farmName' | 'fieldName' | 'size';

/**
 * Stand in rows for showing this screen without a season loaded.
 *
 * Obviously fake on purpose, and the banner says so. Nothing here should be mistaken for a farm.
 */
const DEMO_FIELDS: OrderableField[] = [
  { id: -1, farmName: 'Sample Farm North', fieldName: 'Block A1', size: 12.5 },
  { id: -2, farmName: 'Sample Farm North', fieldName: 'Block A2', size: 8.25 },
  { id: -3, farmName: 'Sample Farm North', fieldName: 'Block B1', size: 20 },
  { id: -4, farmName: 'Sample Farm South', fieldName: 'Orchard 1', size: 4.75 },
  { id: -5, farmName: 'Sample Farm South', fieldName: 'Orchard 2', size: 9.45 },
  { id: -6, farmName: 'Sample Farm South', fieldName: 'Orchard 3', size: 9.5 },
  { id: -7, farmName: 'Sample Farm West', fieldName: 'Vineyard East', size: 31.2 },
  { id: -8, farmName: 'Sample Farm West', fieldName: 'Vineyard West', size: 28 },
  { id: -9, farmName: 'Sample Farm West', fieldName: 'Lands 4', size: null },
  { id: -10, farmName: 'Sample Farm East', fieldName: 'Pivot 1', size: 55.8 },
];

/**
 * The order fields appear in on a Crop Nutrition Plan, for one season.
 *
 * <h3>Why this is its own tab and not part of the data table</h3>
 *
 * The data table renders FIVE tables, split by field type: unassigned, grain feed, fruit, vegetable
 * and vineyard. The order being set here is one flat list across farms AND across those five
 * groups, so it cannot be expressed in that table at all. A separate view is the honest home for
 * it, not just a roomier one.
 *
 * <h3>The order is the user's and it is cardinal</h3>
 *
 * Where one is set here it beats the order derived from Lab Manager sample positions. That is the
 * whole point: a season with no soil sampling has no sample positions to derive anything from, so
 * the agent previously had no way to say what order they wanted. Client decision 21/10/2026, see
 * `Context/cnp-field-ordering-plan-2026-10-21.md`.
 *
 * <h3>Sorting is a VIEW only, and dragging turns off while it is on</h3>
 *
 * Client decision 21/10/2026. A column sort rearranges what you are looking at and never touches
 * the saved order. That makes dragging meaningless while a sort is active, because the position a
 * row is dropped at does not correspond to a position in the order being saved, so dragging is
 * disabled and the banner says why. A disabled control with no explanation is worse than no
 * control.
 *
 * <h3>Nothing persists yet</h3>
 *
 * The order lives on this component until the Season Manager endpoint exists. The one place marked
 * TODO is where the save goes.
 */
@Component({
  selector: 'app-season-field-order',
  templateUrl: './season-field-order.component.html',
  styleUrls: ['./season-field-order.component.css'],
  standalone: false,
  // The list can run to fifty rows with a drag over it. Nothing here mutates in place, every change
  // replaces the array, so OnPush sees everything it needs to.
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SeasonFieldOrderComponent implements OnInit, OnDestroy {
  fields: OrderableField[] = [];
  selected = new Set<number>();

  sortColumn: SortColumn = '';
  sortAscending = true;

  isLoading = false;
  saving = false;
  isDemoData = false;
  dirty = false;

  /** Ids moving in the current drag, empty when nothing is being dragged. */
  private draggingIds = new Set<number>();

  private seasonId = 0;
  private original: OrderableField[] = [];
  private readonly subs = new Subscription();

  constructor(
    private readonly seasonManagerService: SeasonManagerService,
    private readonly seasonSharedDataService: SeasonSharedDataService,
    private readonly snackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.seasonSharedDataService.seasonIdData$.subscribe(id => {
        if (id) {
          this.seasonId = Number(id);
          this.load();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // === Loading ===

  load(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.subs.add(
      forkJoin({
        season: this.seasonManagerService.getSeasonById(this.seasonId, true).pipe(catchError(() => of(null))),
        // Fails open. A dropped order call should leave the screen usable and alphabetical, not
        // empty, and the agent can still set an order from there.
        order: this.seasonManagerService.getFieldOrder(this.seasonId).pipe(catchError(() => of([] as number[]))),
      }).subscribe(({ season, order }) => {
        this.fields = this.applySavedOrder(this.toOrderableFields(season ?? []), order ?? []);
        this.isDemoData = this.fields.length === 0;

        if (this.isDemoData) {
          this.fields = [...DEMO_FIELDS];
        }

        this.original = [...this.fields];
        this.dirty = false;
        this.isLoading = false;
        this.cdr.markForCheck();
      }),
    );
  }

  /**
   * Flattens the season response into one row per FIELD.
   *
   * <p><b>getSeasonById returns an ARRAY OF FARMS</b>, not a season object with farms on it. Each
   * farm carries name and fields.
   *
   * <p><b>field.id is the crop definition, field.fieldId is the field.</b> That catches people out,
   * and it caught me: a field with three definitions appears three times with three different
   * ids and one fieldId. The order is per field, so fieldId is the identity and the duplicates are
   * dropped. Using id would have put the same field in the list three times and then saved
   * definition ids to an endpoint that expects field ids.
   */
  private toOrderableFields(farms: any[]): OrderableField[] {
    const seen = new Set<number>();
    const fields: OrderableField[] = [];

    for (const farm of farms ?? []) {
      for (const field of farm?.fields ?? []) {
        const id = field?.fieldId;
        if (id == null || seen.has(id)) {
          continue;
        }
        seen.add(id);
        fields.push({
          id,
          fieldName: field?.fieldName ?? '',
          farmName: farm?.name ?? '',
          size: this.toNumber(field?.fieldSize),
        });
      }
    }

    // Alphabetical by farm then field, as the order anybody gets before they touch anything.
    // numeric so Blok 2 precedes Blok 10 rather than following it, which a plain text sort gets
    // wrong and these field names are full of numbers. sensitivity base so case does not split
    // names that read the same. Same comparator the column sort uses, so the two agree.
    fields.sort(
      (a, b) =>
        a.farmName.localeCompare(b.farmName, undefined, { numeric: true, sensitivity: 'base' }) ||
        a.fieldName.localeCompare(b.fieldName, undefined, { numeric: true, sensitivity: 'base' }),
    );

    return fields;
  }

  /**
   * Puts the saved order in front, and everything else behind it.
   *
   * <p><b>Fields with no saved position go together at the end, alphabetically.</b> Client
   * decision 22/10/2026. They are the fields nobody has placed yet, so grouping them is what makes
   * them easy to find and place; scattering them through the order would hide them.
   *
   * <p>The incoming list holds only positioned fields. It can also name a field that is no longer
   * on the season, which is ignored rather than trusted: an order outlives the thing it ordered.
   */
  private applySavedOrder(fields: OrderableField[], savedOrder: number[]): OrderableField[] {
    if (!savedOrder.length) {
      return fields;
    }

    const byId = new Map(fields.map(f => [f.id, f]));
    const placed: OrderableField[] = [];

    for (const id of savedOrder) {
      const field = byId.get(id);
      if (field) {
        placed.push(field);
        byId.delete(id);
      }
    }

    // byId now holds only the unplaced ones, still in the alphabetical order they arrived in.
    return [...placed, ...byId.values()];
  }

  // === The view ===

  /**
   * What the table renders: the saved order, or a sorted copy of it.
   *
   * A copy, never a sort in place, so turning the sort off returns the real order rather than
   * whatever the last sort left behind.
   */
  get view(): OrderableField[] {
    if (!this.sortColumn) {
      return this.fields;
    }

    const column = this.sortColumn;
    const direction = this.sortAscending ? 1 : -1;

    return [...this.fields].sort((a, b) => direction * this.compare(a, b, column));
  }

  /**
   * Size compares as a number, the names as text.
   *
   * Numeric collation on the string nearly works, and is wrong in a way nobody would spot: it
   * reads the digit runs either side of the decimal point as two separate numbers, so 9.5 lands
   * below 9.45. Subtracting has no such problem, and says plainly that the column is a quantity.
   *
   * Fields with no size recorded group together at one end rather than counting as nought
   * hectares and scattering through the small ones.
   */
  private compare(a: OrderableField, b: OrderableField, column: Exclude<SortColumn, ''>): number {
    if (column === 'size') {
      if (a.size == null || b.size == null) {
        if (a.size == null && b.size == null) {
          return 0;
        }
        return a.size == null ? 1 : -1;
      }
      return a.size - b.size;
    }

    return (a[column] ?? '').localeCompare(b[column] ?? '', undefined, { numeric: true, sensitivity: 'base' });
  }

  /**
   * fieldSize is typed as a string on SeasonFieldsModel and arrives as one, so it is parsed here
   * rather than at the point of display. An absent or unreadable value becomes null, which the
   * table shows as a dash, instead of letting NaN reach the screen.
   */
  private toNumber(value: unknown): number | null {
    if (value == null || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /** Dragging only makes sense against the real order. See the class docs. */
  get canDrag(): boolean {
    return !this.sortColumn;
  }

  sortBy(column: SortColumn): void {
    if (this.sortColumn === column) {
      this.sortAscending = !this.sortAscending;
    } else {
      this.sortColumn = column;
      this.sortAscending = true;
    }
  }

  clearSort(): void {
    this.sortColumn = '';
    this.sortAscending = true;
  }

  /** The position a field holds in the SAVED order, not in the sorted view. */
  positionOf(field: OrderableField): number {
    return this.fields.indexOf(field) + 1;
  }

  // === Selection ===

  toggle(field: OrderableField): void {
    if (this.selected.has(field.id)) {
      this.selected.delete(field.id);
    } else {
      this.selected.add(field.id);
    }
    // A Set mutates in place, so OnPush needs telling.
    this.cdr.markForCheck();
  }

  isSelected(field: OrderableField): boolean {
    return this.selected.has(field.id);
  }

  toggleAll(): void {
    if (this.selected.size === this.fields.length) {
      this.selected.clear();
    } else {
      this.selected = new Set(this.fields.map(f => f.id));
    }
    this.cdr.markForCheck();
  }

  get allSelected(): boolean {
    return this.fields.length > 0 && this.selected.size === this.fields.length;
  }

  get someSelected(): boolean {
    return this.selected.size > 0 && this.selected.size < this.fields.length;
  }

  // === Moving ===

  /**
   * Drops one row, or every selected row if the one being dragged is among them.
   *
   * <p>Non-contiguous selections land contiguous at the target, keeping their relative order. Three
   * rows picked from positions 2, 7 and 15 and dropped at 4 become 4, 5 and 6 in that order. That
   * is what a file manager does and it is the only behaviour that does not need explaining.
   */
  onDrop(event: CdkDragDrop<OrderableField[]>): void {
    this.draggingIds = new Set();

    if (!this.canDrag || event.previousIndex === event.currentIndex) {
      this.cdr.markForCheck();
      return;
    }

    const dragged = this.fields[event.previousIndex];
    const moving = new Set(
      this.selected.has(dragged.id) && this.selected.size > 1
        ? this.fields.filter(f => this.selected.has(f.id)).map(f => f.id)
        : [dragged.id],
    );

    this.moveBlock(moving, this.insertIndexFor(event, dragged, moving));
  }

  /**
   * Turn CDK's drop index into an index in the array the block is being inserted into.
   *
   * <p><b>This is where it went wrong before, so it is worth being explicit.</b> CDK reports
   * currentIndex in the array AFTER the dragged row is taken out, which is what moveItemInArray
   * relies on. Reading it as an index into the original array loses one place on every downward
   * move, and at the end of the list it is unmissable: two rows dropped past the end of ten landed
   * at 8 and 9 instead of 9 and 10. Reported 21/10/2026.
   *
   * <p>So: find the row the block should land in front of, which is the first row at or after the
   * drop point that is not itself moving, then take its index in what remains. Nothing at or after
   * it means the block goes on the end.
   */
  private insertIndexFor(
    event: CdkDragDrop<OrderableField[]>,
    dragged: OrderableField,
    moving: Set<number>,
  ): number {
    const withoutDragged = this.fields.filter(f => f.id !== dragged.id);
    const remaining = this.fields.filter(f => !moving.has(f.id));

    for (let i = event.currentIndex; i < withoutDragged.length; i++) {
      const candidate = withoutDragged[i];
      if (!moving.has(candidate.id)) {
        return remaining.findIndex(f => f.id === candidate.id);
      }
    }

    return remaining.length;
  }

  /**
   * Lift the moving rows out and put them back as a block at a given index.
   *
   * <p>insertAt is an index into what is LEFT once the block is removed, not into the current list.
   * Keeping that one coordinate system is what stops the off by one coming back: callers convert
   * into it once, here, rather than each doing their own arithmetic.
   */
  private moveBlock(moving: Set<number>, insertAt: number): void {
    const remaining = this.fields.filter(f => !moving.has(f.id));
    const block = this.fields.filter(f => moving.has(f.id));

    const at = Math.min(Math.max(insertAt, 0), remaining.length);
    remaining.splice(at, 0, ...block);

    this.fields = remaining;
    this.dirty = true;
  }

  /**
   * The rest of a multi-selection, marked while one of them is being dragged.
   *
   * <p><b>Marked, not hidden, and that is deliberate.</b> CDK caches every row's rectangle when the
   * drag starts and sorts by comparing the pointer against those cached values. Removing rows from
   * the layout mid drag makes every cached rectangle wrong below the first one, and the drop lands
   * somewhere other than where the line says, which is the bug this change exists to fix. Dimming
   * says the same thing to the user and changes no geometry.
   */
  onDragStarted(field: OrderableField): void {
    this.draggingIds =
      this.selected.has(field.id) && this.selected.size > 1
        ? new Set(this.selected)
        : new Set([field.id]);
    this.cdr.markForCheck();
  }

  onDragEnded(): void {
    this.draggingIds = new Set();
    this.cdr.markForCheck();
  }

  /** True for the rows travelling with the one under the cursor, so they can be dimmed. */
  isTravelling(field: OrderableField): boolean {
    return this.draggingIds.size > 1 && this.draggingIds.has(field.id);
  }

  /**
   * Move a row to a typed position, shifting everything it passes.
   *
   * Assigning a position displaces rather than duplicates, so two rows can never hold the same
   * number and nothing is bumped to a next free value. The list stays 1..n with no gaps. Lifted
   * from the Lab Manager samples table, which solved this first, and it is the control that makes
   * fifty blocks workable where dragging alone does not.
   */
  moveTo(field: OrderableField, typed: unknown): void {
    if (!this.canDrag) {
      return;
    }

    const from = this.fields.indexOf(field);
    if (from === -1) {
      return;
    }

    const requested = Number(typed);
    if (!Number.isFinite(requested)) {
      // Restores the number they overtyped, since the input shows a computed value.
      this.fields = [...this.fields];
      this.cdr.markForCheck();
      return;
    }

    const to = Math.min(Math.max(Math.round(requested), 1), this.fields.length) - 1;
    if (to === from) {
      this.fields = [...this.fields];
      this.cdr.markForCheck();
      return;
    }

    // A typed position is already where the row should END UP, and with only this row removed that
    // is exactly its index in what remains. No correction, unlike the drop path above.
    this.moveBlock(new Set([field.id]), to);
  }

  // === Saving ===

  save(): void {
    if (this.isDemoData) {
      this.snackBar.open('Sample data, so nothing was saved.', 'Close', { duration: 4000 });
      return;
    }

    this.saving = true;
    this.cdr.markForCheck();

    this.subs.add(
      this.seasonManagerService.saveFieldOrder(this.seasonId, this.fields.map(f => f.id)).subscribe({
        next: () => {
          this.original = [...this.fields];
          this.dirty = false;
          this.saving = false;
          this.cdr.markForCheck();
          this.snackBar.open(`Order saved for ${this.fields.length} fields.`, 'Close', { duration: 4000 });
        },
        error: () => {
          // The order is still on screen, so say it was not saved rather than leaving the user to
          // assume it was. Nothing is reverted: their arrangement is not thrown away on a failure.
          this.saving = false;
          this.cdr.markForCheck();
          this.snackBar.open('Could not save the order. It is still on screen, try again.', 'Close', { duration: 6000 });
        },
      }),
    );
  }

  /**
   * Clears the saved order, so the season goes back to alphabetical.
   *
   * <p>Not the same as Undo, which only restores what this screen opened with. This throws the
   * order away for everybody, so it asks first.
   */
  resetToDefault(): void {
    if (this.isDemoData || !confirm('Clear the saved order for this season? Fields will go back to alphabetical.')) {
      return;
    }

    this.saving = true;
    this.cdr.markForCheck();

    this.subs.add(
      this.seasonManagerService.saveFieldOrder(this.seasonId, []).subscribe({
        next: () => {
          this.saving = false;
          this.snackBar.open('Order cleared. Reloading.', 'Close', { duration: 3000 });
          this.load();
        },
        error: () => {
          this.saving = false;
          this.cdr.markForCheck();
          this.snackBar.open('Could not clear the order, try again.', 'Close', { duration: 6000 });
        },
      }),
    );
  }

  /** Back to the order this screen opened with. Not the same as clearing the saved order. */
  undo(): void {
    this.fields = [...this.original];
    this.dirty = false;
  }

  trackById(_index: number, field: OrderableField): number {
    return field.id;
  }
}
